import { LazyQueryExecFunction } from '@apollo/client/react';
import {
  WidgetTemplate,
  WidgetTemplateFilterInputObject,
  WidgetTemplatesQuery,
  WidgetTemplatesQueryVariables,
  WidgetsKindEnum,
  SortDirectionEnum,
} from 'graphql-common';
import { DEFAULT_SEARCH_LIST_VARIABLES } from '@lib/enums/urls';
import getLoadOptionsResult from '@lib/utils/getLoadOptionsResult';
import createSearchFilters, {
  defaultSearchListSchema,
} from '@lib/utils/createSearchFilters';
import { LoadOptionsParams } from '@lib/components/Select/types';

type FetchLazyQuery = LazyQueryExecFunction<
  WidgetTemplatesQuery,
  WidgetTemplatesQueryVariables
>;

/**
 * Maps widget kind to icon name
 */
function getWidgetIcon(kind: string): string {
  const iconMap: Record<string, string> = {
    [WidgetsKindEnum.ActiveInterventions]: 'build',
    [WidgetsKindEnum.Announcement]: 'campaign',
    [WidgetsKindEnum.Clock]: 'schedule',
    [WidgetsKindEnum.ExternalLink]: 'link',
    [WidgetsKindEnum.QuickCreation]: 'add',
    [WidgetsKindEnum.SectorsPerformanceMetricsByDeadlinesMissed]:
      'clock_loader_20',
    [WidgetsKindEnum.SitesPerformanceMetricsByDeadlinesMissed]:
      'clock_loader_20',
    [WidgetsKindEnum.SitesMap]: 'location_on',
    [WidgetsKindEnum.WeatherToday]: 'wb_sunny',
    [WidgetsKindEnum.WeatherWeek]: 'wb_sunny',
  };

  return iconMap[kind] || 'widgets';
}

export const fetchSearchList = async (
  searchQuery: string,
  fetchLazyQuery: FetchLazyQuery,
  page: number,
  additionalFilters?: object[],
) => {
  const options: { variables: WidgetTemplatesQueryVariables } = {
    variables: {
      ...DEFAULT_SEARCH_LIST_VARIABLES,
      page,
      sorts: { title: SortDirectionEnum.Asc },
    },
  };
  const filters: WidgetTemplateFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(...createSearchFilters(searchQuery, [{ fieldName: 'title' }]));
  }
  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }
  if (filters.length) {
    options.variables.filters = filters;
  }
  const { data } = await fetchLazyQuery(options);
  return getLoadOptionsResult(
    data,
    (dataItem: WidgetTemplate) => ({
      value: dataItem.id,
      label: dataItem.title || '',
      data: {
        id: dataItem.id,
        kind: dataItem.kind,
        icon: getWidgetIcon(dataItem.kind),
        title: dataItem.title || '',
        description: dataItem.description || '',
        fields: dataItem.fields || {},
        attachmentsAttached: dataItem.attachmentsAttached || [],
      },
    }),
    searchQuery,
  );
};

export const getWidgetTemplatesLoadOptions =
  (fetchLazyQuery: FetchLazyQuery, additionalFilters?: object[]) =>
  (searchQuery: string, loadedOptions: unknown, params: LoadOptionsParams) =>
    fetchSearchList(
      searchQuery,
      fetchLazyQuery,
      params?.page || 1,
      additionalFilters,
    );
